import React, { useState } from "react";
import styled from "styled-components";
import useCalendar from "../../../hooks/useCalender";
import dayjs from "dayjs";
import { Tooltip } from "@material-ui/core";
import { navigate } from "@reach/router";
import DayEventsModal from "./DayEventsModal";

export default function Day({ day, rowIdx }) {
  const { filteredEvents } = useCalendar();
  const [showAllEvents, setShowAllEvents] = useState(false);

  const formatDate = (dateString) => {
    const parts = dateString?.split("/");
    const formattedDate = parts?.length
      ? `${parts[0]}-${parts[1]}-${parts[2].substring(2)}`
      : "";
    return formattedDate;
  };

  const getCurrentDayClass = () => {
    if (day.format("DD-MM-YY") === dayjs().format("DD-MM-YY")) {
      return true;
    }
    return false;
  };

  const isToday = getCurrentDayClass();
  const dayEvents = filteredEvents.filter(
    (evt) => formatDate(evt?.date) === day.format("DD-MM-YY"),
  );

  const MAX_VISIBLE_EVENTS = 2;
  const visibleEvents = dayEvents.slice(0, MAX_VISIBLE_EVENTS);
  const remainingCount = dayEvents.length - MAX_VISIBLE_EVENTS;

  const handleDayClick = (e) => {
    // Only open modal if clicking the day itself, not an event card
    if (e.target === e.currentTarget || e.currentTarget.contains(e.target)) {
      if (dayEvents.length > 0) {
        setShowAllEvents(true);
      }
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    setShowAllEvents(true);
  };

  return (
    <>
      <DayContainer $hasEvents={dayEvents.length > 0} onClick={handleDayClick}>
        <DayHeader>
          {rowIdx === 0 && <WeekDay>{day.format("ddd").toUpperCase()}</WeekDay>}
          <DateNumber $isToday={isToday}>
            {day.format("DD")}
            {isToday && <TodayDot />}
            {dayEvents.length > 0 && (
              <EventCount $isToday={isToday}>{dayEvents.length}</EventCount>
            )}
          </DateNumber>
        </DayHeader>

        <EventsContainer>
          {visibleEvents.map((evt, idx) => (
            <Tooltip key={idx} placement="top" title={evt.title} arrow>
              <EventCard
                onClick={() =>
                navigate(
                  `/dashboard/cases/${evt.caseId}?caseType=${evt.resolutionKind}`
                )
              }
              >
                <EventIndicator />
                <EventTitle>{evt.title}</EventTitle>
              </EventCard>
            </Tooltip>
          ))}

          {remainingCount > 0 && (
            <MoreEventsButton onClick={handleMoreClick}>
              +{remainingCount} more case{remainingCount !== 1 ? "s" : ""}
            </MoreEventsButton>
          )}
        </EventsContainer>
      </DayContainer>

      <DayEventsModal
        open={showAllEvents}
        onClose={() => setShowAllEvents(false)}
        day={day}
        events={dayEvents}
      />
    </>
  );
}

const DayContainer = styled.div`
  background: white;
  border: 1px solid ${(props) => (props.$hasEvents ? "#00838c" : "#e5e9f2")};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  min-height: 120px;
  max-height: 140px;
  position: relative;
  overflow: hidden;
  cursor: ${(props) => (props.$hasEvents ? "pointer" : "default")};

  &:hover {
    border-color: #00838c;
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.15);
    transform: translateY(-2px);
    z-index: 10;
  }

  ${(props) =>
    props.$hasEvents &&
    `
    background: linear-gradient(135deg, #ffffff 0%, #f0feff 100%);
  `}
`;

const DayHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem 0.5rem;
  background: linear-gradient(180deg, #f8f9fc 0%, transparent 100%);
  flex-shrink: 0;
`;

const WeekDay = styled.p`
  font-size: 0.75rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #293461;
  margin: 0 0 0.5rem 0;
  letter-spacing: 1px;
`;

const DateNumber = styled.div`
  font-size: 1.125rem;
  font-family: openSans-SemiBold, sans-serif;
  color: ${(props) => (props.$isToday ? "#ffffff" : "#293461")};
  background: ${(props) => (props.$isToday ? "#00838c" : "transparent")};
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: relative;
  transition: all 0.3s ease;

  ${(props) =>
    props.$isToday &&
    `
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.4);
    animation: pulse 2s ease-in-out infinite;
  `}

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(0, 131, 140, 0.4);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(0, 131, 140, 0.5);
    }
  }
`;

const TodayDot = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 8px;
  height: 8px;
  background: #ff6b6b;
  border-radius: 50%;
  border: 2px solid white;
`;

const EventCount = styled.div`
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: ${(props) => (props.$isToday ? "#ff6b6b" : "#00838c")};
  color: white;
  font-size: 0.65rem;
  font-family: openSans-SemiBold, sans-serif;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const EventsContainer = styled.div`
  flex: 1;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 131, 140, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 131, 140, 0.5);
  }

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 131, 140, 0.3) transparent;
`;

const EventCard = styled.div`
  background: #00838c;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 131, 140, 0.2);
  flex-shrink: 0;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.35);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const EventIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #ffffff;
  opacity: 0.8;
`;

const EventTitle = styled.span`
  font-size: 0.8rem;
  font-family: openSans-SemiBold, sans-serif;
  color: #ffffff;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-left: 0.25rem;
`;

const MoreEventsButton = styled.div`
  background: rgba(0, 131, 140, 0.1);
  color: #00838c;
  padding: 0.4rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: openSans-SemiBold, sans-serif;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px dashed #00838c;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 131, 140, 0.15);
    border-style: solid;
  }
`;
